import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["player"]
  static values = { postId: Number }
  
  connect() {
    this.setupVideoPlayer()
    this.findNextVideos()
  }
  
  setupVideoPlayer() {
    const player = this.playerTarget
    
    // Enhanced controls
    this.addCustomControls()
    
    // Auto-next functionality
    player.addEventListener('ended', () => {
      this.playNextVideo()
    })
    
    // Play/pause with spacebar (only when not typing in an input)
    document.addEventListener('keydown', (e) => {
      // Don't intercept spacebar if user is typing in an input field
      const typingElements = ['INPUT', 'TEXTAREA', 'SELECT']
      const isTyping = typingElements.includes(e.target.tagName) ||
                      e.target.contentEditable === 'true' ||
                      e.target.closest('[contenteditable="true"]')

      if (e.code === 'Space' && !isTyping && this.isVideoInView()) {
        e.preventDefault()
        this.togglePlayPause()
      }
    })
    
    // Auto-play when scrolled into view
    this.setupIntersectionObserver()
  }
  
  addCustomControls() {
    const container = this.element
    const player = this.playerTarget

    // Add next video button
    const nextButton = document.createElement('button')
    nextButton.className = 'absolute top-2 right-2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity z-10'
    nextButton.innerHTML = '<i class="fas fa-step-forward"></i>'
    nextButton.addEventListener('click', () => this.playNextVideo())
    container.appendChild(nextButton)

    // Add video info overlay (hidden initially)
    const infoOverlay = document.createElement('div')
    infoOverlay.className = 'absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded opacity-0 transition-opacity'
    this.infoOverlay = infoOverlay
    container.appendChild(infoOverlay)

    // Update info overlay when metadata is loaded
    player.addEventListener('loadedmetadata', () => {
      this.updateVideoInfo()
    })

    // Add loading indicator
    const loadingSpinner = document.createElement('div')
    loadingSpinner.className = 'absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hidden'
    loadingSpinner.innerHTML = '<div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>'
    container.appendChild(loadingSpinner)
    this.loadingSpinner = loadingSpinner
  }
  
  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Auto-play when 50% visible
          if (entry.intersectionRatio > 0.5) {
            this.playerTarget.play().catch(() => {
              // Auto-play failed, that's okay
            })
          }
        } else {
          // Pause when not visible
          this.playerTarget.pause()
        }
      })
    }, { threshold: [0, 0.5] })

    observer.observe(this.element)

    // Check if already in view on load
    setTimeout(() => {
      const rect = this.element.getBoundingClientRect()
      const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight && rect.top < window.innerHeight
      if (isInView) {
        this.playerTarget.play().catch(() => {
          // Auto-play failed, that's okay - user can click play
        })
      }
    }, 100)
  }
  
  findNextVideos() {
    // Get all video posts from the current feed
    const allVideoPosts = Array.from(document.querySelectorAll('[data-controller*="enhanced-video"]'))
      .map(el => ({
        postId: parseInt(el.dataset.enhancedVideoPostIdValue),
        element: el,
        player: el.querySelector('[data-enhanced-video-target="player"]')
      }))
      .filter(post => post.player && post.postId !== this.postIdValue)
      .sort((a, b) => {
        // Sort by DOM order (feed order)
        return a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      })
    
    this.nextVideos = allVideoPosts
    this.updateNextVideoButton()
  }
  
  updateNextVideoButton() {
    const nextButton = this.element.querySelector('button')
    if (nextButton) {
      nextButton.style.display = this.nextVideos.length > 0 ? 'block' : 'none'
    }
  }
  
  playNextVideo() {
    if (this.nextVideos.length === 0) {
      this.showMessage('No more videos in feed')
      return
    }
    
    // Find next video that's not currently playing
    const nextVideo = this.nextVideos.find(v => v.player.paused || v.player.currentTime === 0)
    
    if (!nextVideo) {
      this.showMessage('All videos have been watched')
      return
    }
    
    // Pause current video
    this.playerTarget.pause()
    
    // Smooth scroll to next video
    nextVideo.element.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    })
    
    // Play next video after scroll
    setTimeout(() => {
      nextVideo.player.currentTime = 0
      nextVideo.player.play().catch(() => {
        this.showMessage('Could not auto-play next video')
      })
    }, 500)
    
    this.showMessage('Playing next video...')
  }
  
  togglePlayPause() {
    const player = this.playerTarget
    if (player.paused) {
      player.play()
    } else {
      player.pause()
    }
  }
  
  isVideoInView() {
    const rect = this.element.getBoundingClientRect()
    const viewHeight = window.innerHeight
    return rect.top >= 0 && rect.bottom <= viewHeight
  }
  
  updateVideoInfo() {
    const player = this.playerTarget
    if (player.duration && this.infoOverlay) {
      const minutes = Math.floor(player.duration / 60)
      const seconds = Math.floor(player.duration % 60)
      this.infoOverlay.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`
      this.infoOverlay.classList.remove('opacity-0')
      this.infoOverlay.classList.add('opacity-100')
    }
  }

  getVideoInfo() {
    const player = this.playerTarget
    if (player.duration) {
      const minutes = Math.floor(player.duration / 60)
      const seconds = Math.floor(player.duration % 60)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }
    return ''
  }
  
  showMessage(text) {
    // Create or update message overlay
    let messageEl = this.element.querySelector('.video-message')
    if (!messageEl) {
      messageEl = document.createElement('div')
      messageEl.className = 'video-message absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm z-20'
      this.element.appendChild(messageEl)
    }
    
    messageEl.textContent = text
    messageEl.style.display = 'block'
    
    // Hide after 2 seconds
    setTimeout(() => {
      messageEl.style.display = 'none'
    }, 2000)
  }
  
  // Public methods for external control
  play() {
    return this.playerTarget.play()
  }
  
  pause() {
    this.playerTarget.pause()
  }
  
  seekTo(time) {
    this.playerTarget.currentTime = time
  }
}